// ConfirmationPage.js - Premium Design
import React, { useState, useEffect } from 'react';
import { useBooking } from '../hooks/useBooking';
import Layout from '../components/layout/Layout';
import APIService from '../services/api';
import { PAGES } from '../utils/constants';
import { EXTRAS_PRICING } from '../utils/extrasConfig';
import { formatCurrency, formatPhoneDisplay } from '../utils/helpers';
import logoMark from '../assets/images/logo.png';

const ConfirmationPage = () => {
  const { state: { bookingDetails, extrasData }, setCurrentPage, setManageBookingId } = useBooking();
  
  const [loading, setLoading] = useState(false);
  const [apiBookingData, setApiBookingData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBookingData = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const bookingId = urlParams.get('bookingId') || 
                      urlParams.get('orderId') || 
                      bookingDetails?.id;
      
      if (bookingId && !apiBookingData && !loading && !error){
        setLoading(true);
        setError(null);
        try {
          const bookingData = await APIService.request(`/bookings/code/${bookingId}`);
          setApiBookingData(bookingData || null);
        } catch (error) {
          setError(`Failed to fetch booking: ${error.message}`);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchBookingData();
  }, [bookingDetails?.id]);

  const displayData = apiBookingData || bookingDetails;

  const handleManageBooking = () => {
    const bookingCode = displayData?.code || displayData?.id;
    if (bookingCode) {
      setManageBookingId(bookingCode);
      setCurrentPage(PAGES.MANAGE_BOOKING);
    }
  };

  const handlePrintInvoice = () => window.print();
  const handleNewBooking = () => setCurrentPage(PAGES.HOME);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="glass-panel p-8 rounded-3xl text-center">
            <div className="w-12 h-12 border-4 border-white/10 border-t-blue-500 rounded-full animate-spin mb-6 mx-auto"></div>
            <p className="text-white font-bold tracking-widest uppercase text-xs">Authenticating Booking...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !displayData) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="glass-panel p-10 rounded-3xl text-center max-w-md border-red-500/20">
            <div className="text-red-500 text-5xl mb-6">⚠️</div>
            <p className="text-white font-black text-xl mb-2">Booking Not Found</p>
            <p className="text-gray-400 text-sm mb-8">{error || "We couldn't retrieve your booking details."}</p>
            <div className="flex gap-4">
              <button onClick={() => window.location.reload()} className="flex-1 bg-white/5 text-white py-3 rounded-full font-black text-xs uppercase tracking-widest border border-white/10">Retry</button>
              <button onClick={handleNewBooking} className="flex-1 bg-blue-600 text-white py-3 rounded-full font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-500/20">Go Home</button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const currency = displayData?.currency || 'USD';
  
  const childSeatsObj = displayData?.extras?.find(e => e.code === 'CHILD_SEAT');
  const childSeatsQty = childSeatsObj ? Number(childSeatsObj.quantity || 0) : Number(extrasData?.childSeats || 0);

  const boosterSeatsObj = displayData?.extras?.find(e => e.code === 'BOOSTER_SEAT');
  const boosterSeatsQty = boosterSeatsObj ? Number(boosterSeatsObj.quantity || 0) : Number(extrasData?.boosterSeats || 0);

  // Dynamic pricing calculation from database or config fallbacks
  const childSeatsPrice = childSeatsObj 
    ? parseFloat(childSeatsObj.total || (childSeatsObj.unitPrice * childSeatsQty) || 0) 
    : childSeatsQty * (EXTRAS_PRICING.childSeat || 5);
    
  const boosterSeatsPrice = boosterSeatsObj 
    ? parseFloat(boosterSeatsObj.total || (boosterSeatsObj.unitPrice * boosterSeatsQty) || 0) 
    : boosterSeatsQty * (EXTRAS_PRICING.boosterSeat || 5);

  const extrasTotal = childSeatsPrice + boosterSeatsPrice;
  const vehicleClassName = displayData?.priceSnapshot?.vehicleClassName || displayData?.vehicleClassName || 'Premium Sedan';

  const rawBase = parseFloat(displayData?.priceSnapshot?.basePrice || displayData?.basePrice || displayData?.baseFare || 0);
  const rawTotal = parseFloat(displayData?.totalAmount || 0);

  let displayBasePrice = rawBase > 0 ? rawBase : (rawTotal - extrasTotal);
  let displayTotalAmount = rawTotal;

  if (extrasTotal > 0 && (rawBase === 0 || Math.abs(rawBase - rawTotal) < 0.01)) {
    displayTotalAmount = rawTotal + extrasTotal;
    displayBasePrice = rawTotal;
  }
  
  const displayPaidAmount = parseFloat(displayData?.paidAmount) || displayTotalAmount;

  const fromPlace = displayData?.fromPlace || displayData?.pickupLocation || 'N/A';
  const toPlace = displayData?.toPlace || displayData?.dropoffLocation || '';
  
  // Parse pickup date/time
  let pickupDateStr = 'N/A';
  let pickupTimeStr = 'N/A';
  
  if (displayData?.pickupAt) {
    const pickupDateObj = new Date(displayData.pickupAt);
    pickupDateStr = pickupDateObj.toLocaleDateString();
    pickupTimeStr = pickupDateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (displayData?.pickupDate) {
    pickupDateStr = new Date(displayData.pickupDate).toLocaleDateString();
    if (displayData?.pickupTime) {
      pickupTimeStr = displayData.pickupTime;
    }
  }

  return (
    <Layout showHeaderMobile={false}>
      <div className="min-h-screen pb-20 print:bg-white print:pb-0">
        <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
          
          {/* Mobile Status Bar */}
          <div className="lg:hidden flex items-center justify-between mb-8">
            <button onClick={handleNewBooking} className="p-3 bg-white/5 border border-white/10 rounded-full text-white">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="bg-green-500 text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-green-500/20">Confirmed ✓</div>
          </div>

          <main className="glass-panel rounded-[2.5rem] overflow-hidden border-white/5 shadow-2xl print:shadow-none print:border-none print:bg-white print:text-black">
            
            {/* Header / Brand */}
            <div className="px-8 md:px-12 py-10 flex flex-col md:flex-row justify-between items-start gap-8 border-b border-white/5 print:border-gray-200">
              <div 
                className="flex items-center gap-5 cursor-pointer group select-none"
                onClick={() => setCurrentPage(PAGES.HOME)}
              >
                <img 
                  src={logoMark} 
                  alt="Logo" 
                  className="w-16 h-16 object-contain p-2 bg-white/5 rounded-2xl border border-white/10 print:bg-transparent transition-all duration-300 group-hover:scale-105 group-hover:border-blue-500/50 group-hover:shadow-[0_0_15px_rgba(59,130,246,0.2)]" 
                />
                <div>
                  <h1 className="text-2xl md:text-3xl font-black text-white tracking-tighter uppercase print:text-black group-hover:text-blue-500 transition-colors duration-300">LankaWayFinder</h1>
                  <div className="text-[10px] text-blue-500 font-black tracking-[0.3em] uppercase mt-1 transition-all duration-300 group-hover:tracking-[0.35em] group-hover:text-blue-400">Global Chauffeurs</div>
                </div>
              </div>
              <div className="md:text-right">
                <div className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-1">Booking Reference</div>
                <h2 className="text-3xl font-black text-white tracking-tighter print:text-black">{displayData?.code || displayData?.id}</h2>
                <div className="text-xs text-blue-500 font-bold mt-1">
                  {new Date(displayData?.createdAt || Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="p-8 md:p-12">
              
              {/* Massive Success Message */}
              <div className="mb-12 text-center md:text-left">
                <div className="inline-block px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-full mb-6">
                  <span className="text-[10px] text-green-500 font-black uppercase tracking-[0.2em] flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                    Verification Successful
                  </span>
                </div>
                <h2 className="text-4xl md:text-6xl font-black text-white tracking-tight leading-[0.9] print:text-black">Safe Travels,<br/>{displayData?.customerName?.split(' ')[0]}!</h2>
                <p className="text-gray-400 mt-6 max-w-lg font-medium leading-relaxed print:text-gray-600">Your premium chauffeur booking is fully confirmed and scheduled. A digital voucher has been dispatched to your email.</p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 mb-16 print:hidden">
                <button onClick={handleManageBooking} className="flex-1 md:flex-none px-8 py-4 bg-blue-600 text-white rounded-full font-black text-xs uppercase tracking-widest hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/20 active:scale-95">Manage Booking</button>
                <button onClick={handlePrintInvoice} className="flex-1 md:flex-none px-8 py-4 bg-white/5 border border-white/10 text-white rounded-full font-black text-xs uppercase tracking-widest hover:bg-white/10 transition-all">Download PDF</button>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
                <div>
                  <h3 className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] mb-6 border-b border-white/5 pb-2">Passenger Information</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-[10px] text-gray-500 font-bold uppercase mb-1">Lead Traveller</div>
                      <div className="text-lg font-black text-white print:text-black">{displayData?.customerName}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-500 font-bold uppercase mb-1">Contact Details</div>
                      <div className="text-sm text-gray-300 font-medium print:text-black">{displayData?.customerEmail}</div>
                      <div className="text-sm text-blue-400 font-bold mt-1">{formatPhoneDisplay(displayData?.customerPhone)}</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] mb-6 border-b border-white/5 pb-2">Itinerary Details</h3>
                  <div className="space-y-6">
                    <div className="flex gap-4">
                      <div className="w-1 bg-blue-500 rounded-full"></div>
                      <div>
                        <div className="text-[10px] text-gray-500 font-black uppercase mb-1">Pickup Information</div>
                        <div className="text-sm font-bold text-white leading-snug print:text-black">{fromPlace}</div>
                        <div className="text-xs text-blue-500 font-bold mt-1 uppercase">
                          {pickupDateStr} • {pickupTimeStr}
                        </div>
                      </div>
                    </div>
                    {toPlace && (
                      <div className="flex gap-4">
                        <div className="w-1 bg-green-500 rounded-full"></div>
                        <div>
                          <div className="text-[10px] text-gray-500 font-black uppercase mb-1">Final Destination</div>
                          <div className="text-sm font-bold text-white leading-snug print:text-black">{toPlace}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Journey Specifications */}
              <div className="mb-16 bg-white/5 rounded-[2rem] p-8 border border-white/5 print:bg-transparent print:border-gray-200">
                <h3 className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] mb-6 border-b border-white/5 pb-2">Journey Specifications</h3>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
                  <div className="bg-white/5 border border-white/5 rounded-2xl p-4">
                    <div className="text-[9px] text-gray-500 font-black uppercase tracking-widest mb-1">Vehicle Type</div>
                    <div className="text-sm font-black text-white capitalize">{vehicleClassName}</div>
                  </div>
                  
                  <div className="bg-white/5 border border-white/5 rounded-2xl p-4">
                    <div className="text-[9px] text-gray-500 font-black uppercase tracking-widest mb-1">Passengers</div>
                    <div className="text-sm font-black text-white">{displayData?.pax || displayData?.passengers || 1} Pax</div>
                  </div>
                  
                  <div className="bg-white/5 border border-white/5 rounded-2xl p-4">
                    <div className="text-[9px] text-gray-500 font-black uppercase tracking-widest mb-1">Luggage Capacity</div>
                    <div className="text-sm font-black text-white">{displayData?.bags || 0} Bags</div>
                  </div>

                  <div className="bg-white/5 border border-white/5 rounded-2xl p-4">
                    <div className="text-[9px] text-gray-500 font-black uppercase tracking-widest mb-1">Flight Number</div>
                    <div className="text-sm font-black text-white">{displayData?.flightNumber || 'N/A'}</div>
                  </div>
                </div>

                {(childSeatsQty > 0 || boosterSeatsQty > 0 || displayData?.notes) && (
                  <div className="space-y-4 border-t border-white/5 pt-6">
                    {(childSeatsQty > 0 || boosterSeatsQty > 0) && (
                      <div className="flex items-center gap-3">
                        <span className="text-lg">👶</span>
                        <div>
                          <div className="text-[9px] text-gray-500 font-bold uppercase">Child Safety Options</div>
                          <div className="text-xs text-gray-300 font-medium">
                            {childSeatsQty > 0 && `${childSeatsQty} Child Seat(s)`}
                            {childSeatsQty > 0 && boosterSeatsQty > 0 && ' • '}
                            {boosterSeatsQty > 0 && `${boosterSeatsQty} Booster Seat(s)`}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {displayData?.notes && (
                      <div className="flex items-start gap-3">
                        <span className="text-lg mt-0.5">💬</span>
                        <div className="flex-1">
                          <div className="text-[9px] text-gray-500 font-bold uppercase">Message for Chauffeur / Special Notes</div>
                          <div className="text-xs text-gray-300 font-medium mt-1 bg-white/5 p-3 rounded-xl border border-white/5 whitespace-pre-wrap">
                            {displayData.notes}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Financial Summary */}
              <div className="bg-white/5 rounded-[2rem] p-8 border border-white/5 print:bg-transparent print:border-gray-200">
                <h3 className="text-[10px] text-blue-500 font-black uppercase tracking-[0.2em] mb-8 text-center">Payment Summary</h3>
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400 font-medium">Chauffeur Service ({vehicleClassName})</span>
                    <span className="text-white font-bold print:text-black">{formatCurrency(displayBasePrice, currency)}</span>
                  </div>
                  {extrasTotal > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-400 font-medium">Add-ons & Options</span>
                      <span className="text-white font-bold print:text-black">{formatCurrency(extrasTotal, currency)}</span>
                    </div>
                  )}
                  <div className="pt-4 border-t border-white/10 flex justify-between items-end">
                    <div>
                      <div className="text-[10px] text-white font-black uppercase tracking-widest">Total Value</div>
                      <div className="text-[9px] text-gray-500 font-bold uppercase mt-1">Taxes Included</div>
                    </div>
                    <div className="text-3xl font-black text-white tracking-tighter print:text-black">{formatCurrency(displayTotalAmount, currency)}</div>
                  </div>

                  {displayPaidAmount < displayTotalAmount && (
                    <div className="pt-4 border-t border-white/10 space-y-2">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-green-500 font-bold">Paid Deposit</span>
                        <span className="text-green-500 font-bold print:text-black">{formatCurrency(displayPaidAmount, currency)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-400 font-medium">Remaining Balance</span>
                        <span className="text-white font-bold print:text-black">{formatCurrency(displayTotalAmount - displayPaidAmount, currency)}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 border-t border-white/10 pt-8">
                  <div className="bg-green-500/5 border border-green-500/20 rounded-2xl p-4 text-center">
                    <div className="text-[9px] text-green-500 font-black uppercase tracking-widest mb-1">Status</div>
                    <div className="text-sm font-black text-green-500 uppercase">
                      {displayPaidAmount >= displayTotalAmount ? 'Paid Full' : 'Paid Deposit'}
                    </div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                    <div className="text-[9px] text-gray-500 font-black uppercase tracking-widest mb-1">Method</div>
                    <div className="text-sm font-black text-white uppercase print:text-black">{displayData?.paymentMethod || 'Card'}</div>
                  </div>
                </div>
              </div>

              {/* Support */}
              <div className="mt-16 text-center print:hidden">
                <p className="text-xs text-gray-500 font-medium mb-4">Our concierge team is available 24/7 for adjustments</p>
                <div className="flex justify-center gap-6">
                  <a href="mailto:support@lankawayfinder.com" className="text-[10px] text-blue-500 font-black uppercase tracking-widest hover:text-white transition-colors">Email Concierge</a>
                  <a href="tel:+94771234567" className="text-[10px] text-blue-500 font-black uppercase tracking-widest hover:text-white transition-colors">Call Support</a>
                </div>
              </div>

            </div>
          </main>
        </div>
      </div>
    </Layout>
  );
};

export default ConfirmationPage;